import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from './config';
import { fetchBody7Data, fetchBody9Data } from './homeDataService';

// ─────────────────────────────────────────────
// ABOUT PAGE
// ─────────────────────────────────────────────

async function fetchAboutUsData() {
  const [body1Snap, missionSnap, cardsSnapshot] = await Promise.all([
    getDoc(doc(db, 'About', 'body1')),
    getDoc(doc(db, 'About', 'mission')),
    getDocs(collection(db, 'About', 'mission', 'cards'))
  ]);

  const storyData = {
    smallHeading: 'OUR STORY',
    heading: "More Than a Meal, It's the Comfort of Home",
    content: 'BentoBox is a FoodTech company serving Gurgaon, India and surrounding areas including DLF Cyber City, Sector 45, Sohna Road, and all major residential sectors. We are on a simple, powerful mission: to deliver genuine home-cooked meals right to your doorstep in eco-friendly containers. Tired of repetitive restaurant food and unsure about hygiene? We bridge the gap between the comfort of a home kitchen and the convenience of modern food delivery. Our network of dedicated home chefs prepares every meal with care in hygienic kitchens, ensuring you get nutritious, fresh, and delicious food in sustainable packaging.',
    img: ''
  };

  if (body1Snap.exists()) {
    const data = body1Snap.data();
    storyData.smallHeading = data.smallHeading || storyData.smallHeading;
    storyData.heading = data.heading || storyData.heading;
    storyData.content = data.content || storyData.content;
    storyData.img = data.img || storyData.img;
  }

  const missionData = {
    smallHeading: 'WHAT WE STAND FOR',
    heading: 'Our Core Values',
    titleMission: 'Our Mission',
    missionDescription: 'To deliver the authentic taste and warmth of home-cooked meals at your doorstep in Gurgaon, using 100% eco-friendly containers, without ever compromising on quality, freshness, or hygiene.'
  };

  if (missionSnap.exists()) {
    const data = missionSnap.data();
    missionData.smallHeading = data.smallHeading || missionData.smallHeading;
    missionData.heading = data.heading || missionData.heading;
    missionData.titleMission = data.TitleMission || missionData.titleMission;
    missionData.missionDescription = data.MissionDescription || missionData.missionDescription;
  }

  let coreValues = [];
  if (!cardsSnapshot.empty) {
    const cardsList = [];
    cardsSnapshot.forEach((docSnap) => {
      const docId = docSnap.id;
      const data = docSnap.data();
      cardsList.push({
        id: docId,
        title: data.heading || '',
        description: data.content || ''
      });
    });

    cardsList.sort((a, b) => {
      const numA = parseInt(a.id.replace('card', '')) || 0;
      const numB = parseInt(b.id.replace('card', '')) || 0;
      return numA - numB;
    });
    
    // We don't include icons here, the component will map them based on index
    coreValues = cardsList;
  }

  return { storyData, missionData, coreValues };
}

export async function fetchAboutPageData() {
  const [aboutUs, body7, body9] = await Promise.all([
    fetchAboutUsData().catch(e => { console.error('AboutUs error:', e); return null; }),
    fetchBody7Data().catch(e => { console.error('Body7 error:', e); return null; }),
    fetchBody9Data().catch(e => { console.error('Body9 error:', e); return null; })
  ]);
  
  return { aboutUs, body7, body9 };
}

// ─────────────────────────────────────────────
// CONTACT PAGE
// ─────────────────────────────────────────────

async function fetchContactUsData() {
  const contactRef = doc(db, 'Contact Us', 'contactPage');
  const contactSnap = await getDoc(contactRef);
  
  const contactInfo = {
    phone: '+91 9876095163',
    email: 'support@getbentobox.in',
    location: 'Gurgaon, Delhi'
  };

  if (contactSnap.exists()) {
    const data = contactSnap.data();
    contactInfo.phone = data.number || contactInfo.phone;
    contactInfo.email = data.emailId || contactInfo.email;
    contactInfo.location = data.location || contactInfo.location;
  }

  return contactInfo;
}

export async function fetchContactPageData() {
  const [contactUs, body7, body9] = await Promise.all([
    fetchContactUsData().catch(e => { console.error('ContactUs error:', e); return null; }),
    fetchBody7Data().catch(e => { console.error('Body7 error:', e); return null; }),
    fetchBody9Data().catch(e => { console.error('Body9 error:', e); return null; })
  ]);
  
  return { contactUs, body7, body9 };
}

// ─────────────────────────────────────────────
// FAQ PAGE
// ─────────────────────────────────────────────

async function fetchFaqUsData() {
  const [headingSnap, boxSnap, faqsSnapshot, logoSnap] = await Promise.all([
    getDoc(doc(db, 'FAQs', 'FAQ')),
    getDoc(doc(db, 'FAQs', 'FAQ', 'box', 'box')),
    getDocs(collection(db, 'FAQs', 'FAQ', 'All FAQs')),
    getDoc(doc(db, 'home', 'navBar', 'imgLogo', 'imgLogo'))
  ]);

  const faqData = {
    mainHeading: "Frequently Asked Questions",
    contactBox: {
      heading: "Do you more questions?",
      content: "Can't find what you're looking for? Our BentoBox customer support team is here to help!",
      button: "Shoot a direct mail",
      img: ""
    },
    faqs: [],
    logoUrl: ""
  };

  if (headingSnap.exists()) {
    faqData.mainHeading = headingSnap.data().heading || faqData.mainHeading;
  }

  if (boxSnap.exists()) {
    const data = boxSnap.data();
    faqData.contactBox.heading = data.heading || faqData.contactBox.heading;
    faqData.contactBox.content = data.content || faqData.contactBox.content;
    faqData.contactBox.button = data.button || faqData.contactBox.button;
    faqData.contactBox.img = data.img || faqData.contactBox.img;
  }

  if (!faqsSnapshot.empty) {
    const faqList = [];
    faqsSnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      faqList.push({
        id: docSnap.id,
        question: data.heading || '',
        answer: data.content || ''
      });
    });

    faqList.sort((a, b) => {
      const numA = parseInt(a.id.replace('faq', '')) || 0;
      const numB = parseInt(b.id.replace('faq', '')) || 0;
      return numA - numB;
    });

    faqData.faqs = faqList;
  }

  if (logoSnap.exists()) {
    faqData.logoUrl = logoSnap.data().imgLogo || '';
  }

  return faqData;
}

export async function fetchFaqPageData() {
  const [faqUs, body9] = await Promise.all([
    fetchFaqUsData().catch(e => { console.error('FAQ error:', e); return null; }),
    fetchBody9Data().catch(e => { console.error('Body9 error:', e); return null; })
  ]);
  
  return { faqUs, body9 };
}

// ─────────────────────────────────────────────
// BLOG PAGE
// ─────────────────────────────────────────────

async function fetchBlogDataInternal() {
  const [titleSnap, blogsSnapshot] = await Promise.all([
    getDoc(doc(db, 'Blogs', 'Title')),
    getDocs(collection(db, 'Blogs'))
  ]);

  const blogData = {
    pageTitle: 'Blog',
    posts: []
  };

  if (titleSnap.exists() && titleSnap.data().heading) {
    blogData.pageTitle = titleSnap.data().heading;
  }

  const blogPosts = [];
  blogsSnapshot.forEach((docSnap) => {
    const docId = docSnap.id;
    if (docId.startsWith('card')) {
      const data = docSnap.data();
      blogPosts.push({
        id: docId,
        heading: data.heading || '',
        author: data.author || 'Anonymous',
        authorImg: data.authorImg || '',
        date: data.date || '',
        img: data.img || '',
        alt: data.alt || '',
        title: data.title || '',
        slug: data.slug || '',
        type: data.type || 'General',
        featured: docId === 'card1'
      });
    }
  });

  blogPosts.sort((a, b) => {
    const numA = parseInt(a.id.replace('card', '')) || 0;
    const numB = parseInt(b.id.replace('card', '')) || 0;
    return numA - numB;
  });

  blogData.posts = blogPosts;
  return blogData;
}

export async function fetchBlogPageData() {
  const [blog, body7, body9] = await Promise.all([
    fetchBlogDataInternal().catch(e => { console.error('Blog error:', e); return null; }),
    fetchBody7Data().catch(e => { console.error('Body7 error:', e); return null; }),
    fetchBody9Data().catch(e => { console.error('Body9 error:', e); return null; })
  ]);
  
  return { blog, body7, body9 };
}

// ─────────────────────────────────────────────
// LOCATIONS PAGE
// ─────────────────────────────────────────────

async function fetchLocationsDataInternal() {
  const [topSectionSnap, locationsSnapshot] = await Promise.all([
    getDoc(doc(db, 'locations', 'topSection')),
    getDocs(collection(db, 'locations'))
  ]);

  const locData = {
    topSection: {
      Orangeheading: 'Our Address',
      heading: 'Check Out Our Locations'
    },
    locations: []
  };

  if (topSectionSnap.exists()) {
    locData.topSection = { ...locData.topSection, ...topSectionSnap.data() };
  }

  const locationsList = [];
  locationsSnapshot.forEach((docSnap) => {
    const docId = docSnap.id;
    if (docId.startsWith('location')) {
      const data = docSnap.data();
      locationsList.push({
        id: docId,
        city: data.city || '',
        heading: data.heading || '',
        address: data.address || '',
        email: data.email || '',
        whatsappNumber: data.whatsappNumber || '',
        img: data.img || ''
      });
    }
  });

  locationsList.sort((a, b) => {
    const numA = parseInt(a.id.replace('location', '')) || 0;
    const numB = parseInt(b.id.replace('location', '')) || 0;
    return numA - numB;
  });

  locData.locations = locationsList;
  return locData;
}

export async function fetchLocationsPageData() {
  const [locations, body7, body9] = await Promise.all([
    fetchLocationsDataInternal().catch(e => { console.error('Locations error:', e); return null; }),
    fetchBody7Data().catch(e => { console.error('Body7 error:', e); return null; }),
    fetchBody9Data().catch(e => { console.error('Body9 error:', e); return null; })
  ]);
  
  return { locations, body7, body9 };
}

// ─────────────────────────────────────────────
// BULK ORDER PAGE
// ─────────────────────────────────────────────

async function fetchBulkOrderDataInternal() {
  const [headingsSnap, buttonsSnap, bottomSnap, imageSnap] = await Promise.all([
    getDoc(doc(db, 'BulkOrder', 'Headings')),
    getDoc(doc(db, 'BulkOrder', 'Buttons')),
    getDoc(doc(db, 'BulkOrder', 'BottomSeperateHeadings')),
    getDoc(doc(db, 'BulkOrder', 'Image'))
  ]);

  const bulkData = {
    headings: {},
    buttons: {},
    bottomHeadings: {},
    image: {}
  };

  if (headingsSnap.exists()) {
    bulkData.headings = headingsSnap.data();
  }
  if (buttonsSnap.exists()) {
    bulkData.buttons = buttonsSnap.data();
  }
  if (bottomSnap.exists()) {
    bulkData.bottomHeadings = bottomSnap.data();
  }
  if (imageSnap.exists()) {
    bulkData.image = imageSnap.data();
  }

  return bulkData;
}

export async function fetchBulkOrderPageData() {
  const [bulkOrder, body7, body9] = await Promise.all([
    fetchBulkOrderDataInternal().catch(e => { console.error('BulkOrder error:', e); return null; }),
    fetchBody7Data().catch(e => { console.error('Body7 error:', e); return null; }),
    fetchBody9Data().catch(e => { console.error('Body9 error:', e); return null; })
  ]);
  
  return { bulkOrder, body7, body9 };
}
